import { useState } from 'react'
import { useDebounce } from './useDebounce'

/**
 * Search state management with debouncing
 *
 * Provides search text state with automatic debouncing to reduce API calls.
 * The debounced value is what should be used for API queries.
 *
 * @param initialValue - Initial search text (default: empty string)
 * @param debounceDelay - Debounce delay in milliseconds (default: 300ms per spec)
 * @returns Object with searchText, debouncedSearchText, setSearchText, and clearSearch
 *
 * @example
 * ```tsx
 * const { searchText, debouncedSearchText, setSearchText, clearSearch } = useSearch();
 *
 * // Use searchText for controlled input
 * <input value={searchText} onChange={(e) => setSearchText(e.target.value)} />
 *
 * // Use debouncedSearchText for API calls
 * const { data } = useQuery({
 *   queryKey: ['moves', debouncedSearchText],
 *   queryFn: () => fetchMoves({ searchText: debouncedSearchText }),
 * });
 * ```
 */
export function useSearch(
  initialValue: string = '',
  debounceDelay: number = 300
) {
  const [searchText, setSearchText] = useState<string>(initialValue)
  const debouncedSearchText = useDebounce(searchText, debounceDelay)

  const clearSearch = () => {
    setSearchText('')
  }

  return {
    searchText,
    debouncedSearchText,
    setSearchText,
    clearSearch,
    isSearching: searchText.length > 0,
    hasSearchResults: debouncedSearchText.length > 0,
  }
}
